import React, { useState, useEffect } from 'react';
import { projectChapters } from '../../constants';

function ChapterNavigation({ activeSection, scrollToSection, project }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Filter chapters based on what exists in the project
  const availableChapters = projectChapters.filter((chapter) => {
    // Always show overview, challenge, and results if they exist
    if (chapter.id === 'overview') return true;
    if (chapter.id === 'the-problem') return project?.userResearch;
    if (chapter.id === 'challenge') return project?.challenge;
    if (chapter.id === 'results') return project?.results;
    if (chapter.id === 'visual-treatment') return project?.visualTreatment;
    if (chapter.id === 'final-design') return project?.finalDesign || project?.results?.hpImage || (project?.results?.finalDesignImages && project.results.finalDesignImages.length > 0) || (project?.results?.finalDesignRows && project.results.finalDesignRows.length > 0);
    
    // Only show problem chapters if they exist in the project
    if (chapter.id.startsWith('problem-')) {
      return project?.problems?.some(p => p.id === chapter.id);
    }
    
    // Show achievements, reflections, design process if they exist
    if (chapter.id === 'achievements') return project?.achievements;
    if (chapter.id === 'reflections') return project?.reflections;
    if (chapter.id === 'design-process') return project?.designProcess;

    return false;
  });

  // Close mobile menu when clicking outside or on a link
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('nav')) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  const handleChapterClick = (chapterId) => {
    scrollToSection(chapterId);
    setIsMobileMenuOpen(false);
  };

  const renderChapterMenu = () => (
    <div className="flex flex-col items-start gap-2">
      {availableChapters.map((chapter, index) => (
        <React.Fragment key={chapter.id}>
          <button
            onClick={() => handleChapterClick(chapter.id)}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-normal rounded-lg transition-all ${
              activeSection === chapter.id
                ? 'text-[#5A58B8] bg-[#918FFB]/15'
                : 'text-[#0D0D0D] hover:text-[#5A58B8] hover:bg-[#918FFB]/15'
            }`}
          >
            <span className="material-symbols-outlined text-base">
              {chapter.icon}
            </span>
            <span>{chapter.label}</span>
          </button>
          {index < availableChapters.length - 1 && (
            <div className="w-full h-px bg-[#E5E5E5]"></div>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="fixed left-6 top-1/2 transform -translate-y-1/2 z-50 hidden md:block">
        <div className="bg-white rounded-lg px-3 py-4 shadow-md border border-[#E5E5E5]">
          {renderChapterMenu()}
        </div>
      </nav>

      {/* Mobile Navigation - Chapter Button */}
      <nav className="fixed bottom-6 right-6 z-50 md:hidden">
        <div className="relative">
          {/* Chapter Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="w-14 h-14 bg-white rounded-full shadow-lg border border-[#E5E5E5] flex items-center justify-center hover:opacity-80 transition-opacity"
            aria-label="Toggle chapter navigation"
          >
            <span 
              className="material-symbols-outlined text-2xl text-[#0D0D0D] transition-all duration-300 ease-in-out"
            >
              {isMobileMenuOpen ? 'close' : 'bookmark'}
            </span>
          </button>

          {/* Mobile Menu Dropdown */}
          <div 
            className="absolute bottom-16 right-0 bg-white rounded-lg px-3 py-4 shadow-lg border border-[#E5E5E5] min-w-[200px] mb-2 transition-all duration-300 ease-out overflow-hidden"
            style={{
              maxHeight: isMobileMenuOpen ? '500px' : '0',
              opacity: isMobileMenuOpen ? 1 : 0,
              transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(10px)',
              pointerEvents: isMobileMenuOpen ? 'auto' : 'none'
            }}
          >
            {renderChapterMenu()}
          </div>
        </div>
      </nav>
    </>
  );
}

export default ChapterNavigation;

